import { PrintSelectImageData } from "../../../components/pages/sift/print-select-images/hooks/use-print-select-images";
import { TrimTempModel } from "../../../trim-editor/model/trim.temp-model";
import { RecordData } from "../../../utilities/utility.type";
import { UuidGenerator } from "../../../utilities/uuid-generator";
import { Endpaper2Response, EndpaperResponse } from "../../api/back/create-order/endpaper";
import { ProductDetailItemData, ProductDetailResponse } from "../../api/back/create-order/product-detail";
import { PrintItemElementDataEntity } from "../../api/sift/back/api-elements";
import { ItemSettingsDataEntity } from "../../api/sift/back/api-item-settings";
import { ProcessingDataEntity } from "../../api/sift/back/api-processing";
import { ProductDataEntity } from "../../api/sift/back/api-products";
import { TemplateGroupDataEntity } from "../../api/sift/back/api-templates";

type PrintProcessStatusData = { date: string, machineId: string };
export type PrintProcessStatus = {
  start: PrintProcessStatusData,
  finish?: PrintProcessStatusData,
}
// 注文ごとデータ群
export type PrintDataBase = {
  kijshopCd: string,
  shopOrderId: string,
  deliveryData?: PrintDeliveryDataEntity,
  item?: RecordData<PrintItemDataEntity>,
  meta?: PrintCartMetaData,
  info?: PrintOrderInfoDataEntity,
  toneCorrection?: ToneCorrectionDataEntity[],
}

// カート単位でのメタ情報
export type PrintCartMetaData = {
  customerMemo?: string,
  checkOrderMemo?: boolean,
  checkItemMemo?: boolean,
}

// 注文内商品ごとデータ群
// アイテムごとステータス 0:注文作成済 10: ラボ発注可 20: ラボ発注中
type PrintItemStatus = 0 | 10 | 20;
export const PRINT_ITEM_STATUS = {
  created: 0,
  enableOrder: 10,
  ordering: 20,
} as const;
export const PRINT_ITEM_STATUS_LIST: { label: string, value: typeof PRINT_ITEM_STATUS[keyof typeof PRINT_ITEM_STATUS] }[] = [
  { value: 0, label: '注文作成済' },
  { value: 10, label: 'ラボ発注可' },
  { value: 20, label: 'ラボ発注中' },
]
export type PrintItemDataEntity = {
  id: string,
  orderMethod: '40',
  status: PrintItemStatus,
  process?: PrintProcessStatus;
  productData?: PrintProductDataEntity,
  templates?: PrintTemplateDataEntity[],
  images: PrintProductImageDataEntity[],
  memo?: string,
}

export type PrintTemplateDataEntity = TemplateGroupDataEntity & {
  template: TrimTempModel,
}

export type PrintItemAdditionalInfo = {
  info1: EndpaperResponse['additionalInfos'],
  info2: Endpaper2Response['additionalInfos'],
}

// 商品データ from キャロルマスタから取得
export type PrintProductDataEntity = {
  serviceName: string,
  serviceId: string,
  product: ProductDataEntity,
  itemSetting: ItemSettingsDataEntity,
  additional: PrintItemAdditionalInfo,
  itemElements: PrintItemElementDataEntity[],
  ProductDetailItemData: ProductDetailItemData,
  productDetailParent: ProductDetailResponse,
  meta?: {
    trimBaseType: 1 | 2 | 3,
    paperType: string,
    borderType: string,
    laminateType: string,
    layoutMargin: boolean,
    backPrint: boolean,
    autoTrim: boolean,
    datePrint: boolean,
    additionalInfo?: string,
    additionalInfo2?: string,
  },
}

export type ToneCorrectionDataEntity = {
  id: string,
  memo: string,
  value: boolean,
}

export type PrintOrderInfoDataEntity = {
  createDate: string,
  orderGUID: string,
  receptionSerialID: string,
  approval: boolean,
  proxyMode: boolean,
}

// 配送情報
export type PrintDeliveryDataEntity = {
  type?: 'shop' | 'customer',
  customerName?: string,
  zipCode?: string,
  prefecture?: string,
  address1?: string,
  address2?: string,
  phoneNumber?: string,
  mailAddress?: string,
  timeZone?: string,
  start?: string,
  end?: string,
  dispTimeZone?: string,
  shopName?: string,
  shopCode?: string,
  deliveryFormGUID?: string,
  formName?: string,
  pdfPath?: string,
  orderFormGUID?: string,
  originalPath?: string,
  importPath?: string,
  isBillingShopSelected?: string,
}

/** トリミングの状態
 * 1: トリミング無し
 * 2: 手動トリミング済
 * 3: おまかせトリミング
 */
type TrimmingStatus = 1 | 2 | 3;

export type PrintProductImageDataEntity = {
  id: string,
  count: number,
  trimmingStatus: TrimmingStatus,
  datePrint: boolean,
  rotate: '0' | '1',
  thumbId?: string,
}

export class PrintDataStructure implements PrintDataBase {
  fileName: string;
  kijshopCd: string;
  shopOrderId: string;
  deliveryData?: PrintDeliveryDataEntity;
  item?: RecordData<PrintItemDataEntity>;
  meta?: PrintCartMetaData;
  info?: PrintOrderInfoDataEntity;
  toneCorrection: ToneCorrectionDataEntity[] = [];
  constructor(
    fileName: string,
    data: PrintDataBase,
  ) {
    this.kijshopCd = data.kijshopCd;
    this.shopOrderId = data.shopOrderId;
    this.deliveryData = data.deliveryData;
    this.item = data.item;
    this.fileName = fileName;
    this.meta = data.meta;
    this.info = data.info;
    this.toneCorrection = data.toneCorrection ?? [];
    // console.log('[PRINT_MODEL]', this);
  }

  exportJson() {
    return {
      filename: this.fileName || `${this.shopOrderId}.json`,
      body: JSON.stringify(this),
    }
  }

  getItemList() {
    if (!this.item) return [];
    return Object.values(this.item);
  }

  getUseImageIdList() {
    const result: string[] = [];
    this.getItemList().map((v) => v.images).flat().forEach((v) => {
      if (v.count && !result.find((v2) => v2 === v.id)) {
        result.push(v.id);
      }
    });
    return result;
  }

  createItem(): PrintItemDataEntity {
    const id = UuidGenerator.create();
    if (this.item?.[id]) {
      return this.createItem();
    }
    const item: PrintItemDataEntity = {
      id,
      orderMethod: '40',
      images: [],
      status: 0,
    }
    if (!this.item) this.item = {};
    this.item[id] = item;
    return item;
  }

  deleteItem(id: string) {
    const target = this.item?.[id];
    if (!target || !this.item) return this.getItemList();
    delete this.item[id];
    return this.getItemList();
  }

  getInvalidCompleteItems(itemId: string, imageList: PrintSelectImageData[]) {
    const items = this.getItemList();
    const filter = items.filter((v) => {
      const images = v.id === itemId ? (imageList as PrintProductImageDataEntity[]) : v.images;
      return images.every((v) => !v.count);
    });
    return filter.map((v) => ({ id: v.id ,name: v.productData?.ProductDetailItemData?.itemName ?? ''}));
  }
  getTemplate(itemId: string, directFlg: '1' | '2', printDateFlg: boolean) {
    const item = this.item?.[itemId];
    if (!item) throw new Error('対象の商品が存在しません。');
    const _templates = item.templates;
    const templates = _templates?.filter(v => !!Number(v.printDateFlg) === !!printDateFlg);
    const target = !directFlg
      ? templates?.[0]
      : templates?.find((v) => v.directFlg === directFlg) ?? templates?.[0];
    if (!target) throw new Error('対象のテンプレートが存在しません。');
    return target;
  }
  static create(
    kijshopCd: string,
    shopOrderId: string,
  ) {
    return new PrintDataStructure(`${shopOrderId}.json`, {
      kijshopCd,
      shopOrderId,
    });
  }
}
